// Pestaña Taller de la Sala de Control (Pau 24-10-2026: «ver de un vistazo cuántos agentes hay, cuántas branches,
// qué agente toca cuál y cómo va todo hasta la versión oficial, sin lenguaje técnico»; luego «mucho más visual» y
// «¿qué son estos trabajos: worktrees o branches?»). Por eso cada bloque dice qué es con sus palabras, con el nombre de
// GitHub al lado y un enlace para comprobarlo allí. De arriba abajo: el semáforo y las líneas de trabajo en GitHub → lo
// que necesita tu atención → cada sala (su versión oficial, sus agentes y sus líneas en GitHub) → cómo entra un cambio →
// el ritmo y cuánto llevan abandonadas → lo que entró. Todo sale del servidor (backend/src/oficina/taller.ts); aquí solo
// se pinta. Colores: los de estado de la oficina en el orden validado con daltonismo (rojo, oro, azul, verde) y gris
// para lo que no avanza (con borde o rayado, nunca solo gris); cada color va con su nombre y su cifra. Se actualiza solo
// cada minuto y con «Actualizar ya». Horas en hora de Tailandia; los días del ritmo, en UTC (el día del panel).
import { AlertTriangle, CheckCircle2, ChevronRight, CircleDashed, Clock, ExternalLink, Hourglass, MessageCircleQuestion, RefreshCw, UserX, WifiOff, XCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { CSSProperties, ReactNode } from 'react';
import { t, type TKey } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { rutaDelAgente } from '../abrirDesdeAviso';
import { Aviso, IconoEncargo } from '../ui';
import { EnMarcha } from './EnMarcha';
import { edad } from './formato';
import { cuandoPau } from './Obra';
import { ZONA_PAU } from './obraResumen';
import { Calculando, ListaPlegable, SinDatos } from './piezas';
import type { AvisoTaller, PasoTaller, SalaTallerSala, TallerSala, TramoOlvidoTaller, TrabajoTaller } from './tipos';
import { calculandoPrimeraVez, useSalaRincon } from './useSalaRincon';

/** Los pasos en grupos que se ven (revisión y entrada son «en camino»), en el orden de los colores validados. */
export type GrupoTaller = 'atascado' | 'te_espera' | 'en_camino' | 'haciendo' | 'quieto' | 'sin_nadie';
const GRUPO_DE: Record<PasoTaller, GrupoTaller> = {
  atascado: 'atascado',
  te_espera: 'te_espera',
  revisando: 'en_camino',
  entrando: 'en_camino',
  haciendo: 'haciendo',
  quieto: 'quieto',
  sin_nadie: 'sin_nadie',
};
/** Trazo (SVG), relleno (HTML), texto y borde de cada grupo. «sin_nadie»: rayado en el círculo, con borde en los puntos. */
const COLOR: Record<GrupoTaller, { trazo: string; punto: string; texto: string; borde: string }> = {
  atascado: { trazo: 'stroke-oficina-error', punto: 'bg-oficina-error', texto: 'text-oficina-error-claro', borde: 'border-oficina-error' },
  te_espera: { trazo: 'stroke-oficina-te-necesita', punto: 'bg-oficina-te-necesita', texto: 'text-oficina-te-necesita', borde: 'border-oficina-te-necesita' },
  en_camino: { trazo: 'stroke-oficina-serie-1', punto: 'bg-oficina-serie-1', texto: 'text-oficina-serie-1', borde: 'border-oficina-serie-1' },
  haciendo: { trazo: 'stroke-oficina-trabajando', punto: 'bg-oficina-trabajando', texto: 'text-oficina-trabajando', borde: 'border-oficina-trabajando' },
  quieto: { trazo: 'stroke-oficina-esperando', punto: 'bg-oficina-esperando', texto: 'text-oficina-texto2', borde: 'border-oficina-esperando' },
  sin_nadie: { trazo: '', punto: 'border-2 border-oficina-texto3 bg-oficina-gasto-apagado', texto: 'text-oficina-texto3', borde: 'border-oficina-linea' },
};
/** Las salas en el ritmo (serie-2 y serie-4, validadas juntas); de la tercera en adelante, gris. */
const COLOR_SALA = ['fill-oficina-serie-2', 'fill-oficina-serie-4'];
const PUNTO_SALA = ['bg-oficina-serie-2', 'bg-oficina-serie-4'];
const ID_RAYAS = 'taller-rayas';

const segundosDesde = (iso: string, ahora: number) => Math.max(0, (ahora - Date.parse(iso)) / 1000);
/** «3-11» (día en Tailandia). */
const diaPau = (iso: string) =>
  new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'numeric', timeZone: ZONA_PAU }).replace('/', '-');
/** «24-10» de un día AAAA-MM-DD. */
const diaCorto = (dia: string) => `${Number(dia.slice(8, 10))}-${Number(dia.slice(5, 7))}`;

function Caja({ titulo, sub, children }: { titulo: string; sub?: string; children: ReactNode }) {
  return (
    <section className="rounded-o-m bg-oficina-elevada px-4 py-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3">
        <h4 className="text-o-nota font-bold text-oficina-texto3">{titulo}</h4>
        {sub ? <span className="text-o-nota text-oficina-texto3">{sub}</span> : null}
      </div>
      {children}
    </section>
  );
}

/** Etiqueta flotante al pasar el ratón o al enfocar (en el móvil, al tocar). */
function Pista({ texto, children, className, style }: { texto: string; children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <span
      tabIndex={0}
      aria-label={texto}
      style={style}
      className={cn('group/pista relative inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-oficina-acento/60', className)}
    >
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 hidden w-max max-w-[240px] -translate-x-1/2 rounded-md bg-oficina-hover px-2 py-1 text-o-nota text-oficina-texto shadow-o-flota group-hover/pista:block group-focus/pista:block"
      >
        {texto}
      </span>
    </span>
  );
}

/** Las rayas de «sin nadie» en el círculo. */
function Rayas() {
  return (
    <svg width="0" height="0" className="absolute" aria-hidden>
      <defs>
        <pattern id={ID_RAYAS} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="6" height="6" className="fill-oficina-gasto-apagado" />
          <line x1="0" y1="0" x2="0" y2="6" className="stroke-oficina-texto3" strokeWidth="2" />
        </pattern>
      </defs>
    </svg>
  );
}

/** La frase de un aviso, en cristiano; con un solo trabajo, lo nombra. */
export function fraseAviso(a: AvisoTaller): string {
  if (a.tipo === 'demasiados' || a.tipo === 'sin_github') return t(`salaControl.taller.aviso.${a.tipo}`, { n: a.n });
  return a.titulo ? t(`salaControl.taller.aviso.${a.tipo}1` as TKey, { titulo: a.titulo }) : t(`salaControl.taller.aviso.${a.tipo}` as TKey, { n: a.n });
}


/** Las líneas de trabajo en GitHub, por quién las lleva (mismo orden en el círculo, la leyenda y la barra de cada sala). */
type Linea = 'conAgente' | 'conPropuesta' | 'abandonadas' | 'vacias';
const LINEAS: Linea[] = ['conAgente', 'conPropuesta', 'abandonadas', 'vacias'];
const COLOR_LINEA: Record<Linea, { trazo: string | null; punto: string }> = {
  conAgente: { trazo: 'stroke-oficina-trabajando', punto: 'bg-oficina-trabajando' },
  conPropuesta: { trazo: 'stroke-oficina-serie-1', punto: 'bg-oficina-serie-1' },
  abandonadas: { trazo: null, punto: 'border-2 border-oficina-texto3 bg-oficina-gasto-apagado' },
  vacias: { trazo: 'stroke-oficina-gasto-hueco', punto: 'bg-oficina-gasto-hueco' },
};
type CuentaLineas = Record<Linea, number>;
const sumaLineas = (salas: SalaTallerSala[]): CuentaLineas =>
  Object.fromEntries(LINEAS.map((l) => [l, salas.reduce((n, s) => n + s.lineas[l], 0)])) as CuentaLineas;

/** El círculo de las líneas de trabajo en GitHub, partido por quién las lleva (con aire entre trozos). */
function Donut({ cuentas, total }: { cuentas: CuentaLineas; total: number }) {
  const R = 52;
  const C = 2 * Math.PI * R;
  const hueco = LINEAS.filter((l) => cuentas[l]).length > 1 ? 3 : 0;
  let acumulado = 0;
  return (
    <svg viewBox="0 0 140 140" className="h-36 w-36 shrink-0" role="img" aria-label={t('salaControl.taller.donutAria', { n: total })}>
      <circle cx="70" cy="70" r={R} fill="none" className="stroke-oficina-panel" strokeWidth="18" />
      {LINEAS.filter((l) => cuentas[l] > 0).map((l) => {
        const largo = (cuentas[l] / Math.max(1, total)) * C;
        const trozo = (
          <circle
            key={l}
            cx="70"
            cy="70"
            r={R}
            fill="none"
            stroke={COLOR_LINEA[l].trazo ? undefined : `url(#${ID_RAYAS})`}
            className={COLOR_LINEA[l].trazo ?? undefined}
            strokeWidth="18"
            strokeDasharray={`${Math.max(0.5, largo - hueco)} ${C}`}
            strokeDashoffset={-acumulado}
            transform="rotate(-90 70 70)"
          >
            <title>{`${t(`salaControl.taller.lineas.${l}`)}: ${cuentas[l]}`}</title>
          </circle>
        );
        acumulado += largo;
        return trozo;
      })}
      <text x="70" y="70" textAnchor="middle" className="fill-oficina-texto text-o-cifra font-bold">
        {total}
      </text>
      <text x="70" y="90" textAnchor="middle" className="fill-oficina-texto3 text-o-mini">
        {t('salaControl.taller.donutCentro')}
      </text>
    </svg>
  );
}

function LeyendaLineas({ cuentas }: { cuentas: CuentaLineas }) {
  return (
    <ul className="grid min-w-[15rem] grid-cols-1 gap-y-1.5 text-o-texto">
      {LINEAS.map((l) => (
        <li key={l} className="flex items-center gap-2">
          <span aria-hidden className={cn('h-3 w-3 shrink-0 rounded-full', COLOR_LINEA[l].punto)} />
          <span className="text-oficina-texto2">{t(`salaControl.taller.lineas.${l}`)}</span>
          <span className={cn('ml-auto pl-3 font-semibold tabular-nums', cuentas[l] ? 'text-oficina-texto' : 'text-oficina-texto3')}>{cuentas[l]}</span>
        </li>
      ))}
    </ul>
  );
}

function Cifra({ n, texto, tono }: { n: number; texto: string; tono?: string }) {
  return (
    <span className="rounded-o-s bg-oficina-panel px-3 py-1.5">
      <span className={cn('text-o-titulo2 font-bold tabular-nums', tono)}>{n}</span> <span className="text-o-sub text-oficina-texto2">{texto}</span>
    </span>
  );
}

function Portada({ d }: { d: TallerSala }) {
  const primero = d.avisos[0];
  const lineas = sumaLineas(d.salas);
  const tono = { verde: 'bg-oficina-trabajando', ambar: 'bg-oficina-acento', rojo: 'bg-oficina-error' }[d.salud];
  return (
    <section
      data-testid="taller-semaforo"
      className={cn(
        'grid grid-cols-1 items-center gap-4 rounded-o-m border px-4 py-4 lg:grid-cols-[1fr_auto]',
        { verde: 'border-oficina-trabajando/40 bg-oficina-elevada', ambar: 'border-oficina-marca-oscura bg-oficina-burbuja-pau', rojo: 'border-oficina-error/60 bg-oficina-elevada' }[d.salud],
      )}
    >
      <div className="flex min-w-0 items-start gap-4">
        <span aria-hidden className="relative mt-0.5 flex h-14 w-14 shrink-0 items-center justify-center">
          <span className={cn('absolute inset-0 rounded-full opacity-25', tono)} />
          <span className={cn('h-9 w-9 rounded-full', tono)} />
        </span>
        <div className="min-w-0">
          <p className={cn('text-o-titulo2 font-semibold leading-tight', { verde: 'text-oficina-trabajando', ambar: 'text-oficina-acento', rojo: 'text-oficina-error-claro' }[d.salud])}>
            {t(`salaControl.taller.salud.${d.salud}`)}
          </p>
          <p className="mt-1 text-o-cuerpo text-oficina-texto2">{primero ? `${primero.sala}: ${fraseAviso(primero)}` : t('salaControl.taller.saludVerde')}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Cifra n={d.cuentas.agentes} texto={t('salaControl.taller.cifraAgentes')} />
            <Cifra n={d.cuentas.lineas} texto={t('salaControl.taller.cifraLineas')} />
            <Cifra n={lineas.abandonadas} texto={t('salaControl.taller.cifraAbandonadas')} tono={lineas.abandonadas ? 'text-oficina-acento' : undefined} />
            <Cifra n={d.cuentas.entraron} texto={t('salaControl.taller.cifraEntraron')} />
          </div>
        </div>
      </div>
      <div className="flex flex-col items-center gap-4 sm:flex-row">
        <Donut cuentas={lineas} total={d.cuentas.lineas} />
        <div>
          <p className="mb-1.5 text-o-nota font-semibold text-oficina-texto3">{t('salaControl.taller.donutTitulo')}</p>
          <LeyendaLineas cuentas={lineas} />
        </div>
      </div>
    </section>
  );
}

const ICONO_AVISO: Record<AvisoTaller['tipo'], typeof AlertTriangle> = {
  atascado: AlertTriangle,
  te_espera: MessageCircleQuestion,
  quieto: Clock,
  sin_nadie: UserX,
  demasiados: Hourglass,
  sin_github: WifiOff,
};

function QueMirar({ d }: { d: TallerSala }) {
  return (
    <Caja titulo={t('salaControl.taller.mirar')}>
      {d.avisos.length ? (
        <ul className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
          {d.avisos.map((a) => {
            const Icono = ICONO_AVISO[a.tipo];
            return (
              <li
                key={`${a.sala}-${a.tipo}`}
                className={cn('flex gap-3 rounded-o-s border-l-[3px] bg-oficina-panel px-3 py-2.5 text-o-texto', a.gravedad === 'rojo' ? 'border-oficina-error' : 'border-oficina-acento')}
              >
                <Icono aria-hidden className={cn('mt-0.5 h-5 w-5 shrink-0', a.gravedad === 'rojo' ? 'text-oficina-error' : 'text-oficina-acento')} />
                <span>
                  <span className="font-semibold">{a.sala}:</span> {fraseAviso(a)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-o-texto text-oficina-trabajando">{t('salaControl.taller.nadaQueMirar')}</p>
      )}
    </Caja>
  );
}

/** Un punto por trabajo (como mucho MAX_PUNTOS; el resto, «+N»); al pasar por encima, su nombre. */
const MAX_PUNTOS = 24;
function Puntos({ trabajos, grupo }: { trabajos: Array<{ id: string; titulo: string }>; grupo: GrupoTaller }) {
  if (!trabajos.length) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {trabajos.slice(0, MAX_PUNTOS).map((w) => (
        <Pista key={w.id} texto={w.titulo}>
          <span className={cn('block h-3.5 w-3.5 rounded-full', COLOR[grupo].punto)} />
        </Pista>
      ))}
      {trabajos.length > MAX_PUNTOS ? <span className="text-o-nota text-oficina-texto3">{`+${trabajos.length - MAX_PUNTOS}`}</span> : null}
    </div>
  );
}

/** Cómo entra un cambio en la versión oficial: los cuatro pasos, cada uno con una frase, su cifra y un punto por agente. */
function Camino({ d }: { d: TallerSala }) {
  const vivos = d.salas.flatMap((s) => s.trabajos.filter((w) => w.agente?.vivo));
  const de = (paso: PasoTaller) => d.salas.flatMap((s) => s.trabajos).filter((w) => w.paso === paso);
  const entraron = d.salas.flatMap((s) => s.entraron.map((e) => ({ id: e.url, titulo: e.titulo })));
  const etapas: Array<{ clave: 'haciendo' | 'revisando' | 'entrando' | 'entraron'; grupo: GrupoTaller; trabajos: Array<{ id: string; titulo: string }> }> = [
    { clave: 'haciendo', grupo: 'haciendo', trabajos: de('haciendo') },
    { clave: 'revisando', grupo: 'en_camino', trabajos: de('revisando') },
    { clave: 'entrando', grupo: 'en_camino', trabajos: de('entrando') },
    { clave: 'entraron', grupo: 'haciendo', trabajos: entraron },
  ];
  const parados: GrupoTaller[] = ['te_espera', 'atascado', 'quieto'];
  return (
    <Caja titulo={t('salaControl.taller.flujo')} sub={t('salaControl.taller.flujoSub')}>
      <ol className="grid grid-cols-1 gap-2 sm:grid-cols-4" data-testid="taller-flujo">
        {etapas.map((e, i) => (
          <li key={e.clave} className={cn('relative rounded-o-s px-3 py-2.5', e.clave === 'entraron' ? 'bg-oficina-trabajando/10 ring-1 ring-oficina-trabajando/30' : 'bg-oficina-panel')}>
            <p className="text-o-nota font-bold text-oficina-texto3">{t('salaControl.taller.pasoN', { n: i + 1 })}</p>
            <p className="text-o-texto font-semibold">{t(`salaControl.taller.flujoPasos.${e.clave}`)}</p>
            <p className="text-o-nota leading-snug text-oficina-texto3">{t(`salaControl.taller.flujoQue.${e.clave}`)}</p>
            <p className={cn('mt-1 text-o-titulo font-bold leading-tight tabular-nums', e.trabajos.length ? COLOR[e.grupo].texto : 'text-oficina-texto3')}>{e.trabajos.length}</p>
            <Puntos trabajos={e.trabajos} grupo={e.grupo} />
            {i < etapas.length - 1 ? (
              <ChevronRight aria-hidden className="absolute -right-3.5 top-1/2 z-10 hidden h-5 w-5 -translate-y-1/2 text-oficina-texto3 sm:block" />
            ) : null}
          </li>
        ))}
      </ol>
      <p className="mb-1.5 mt-4 text-o-nota font-semibold text-oficina-texto3">{t('salaControl.taller.fuera', { n: vivos.length })}</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {parados.map((g) => {
          const lista = vivos.filter((w) => GRUPO_DE[w.paso] === g);
          return (
            <div key={g} className="rounded-o-s bg-oficina-panel px-3 py-2">
              <p className="flex items-baseline justify-between gap-2 text-o-sub font-semibold text-oficina-texto2">
                {t(`salaControl.taller.grupos.${g}`)}
                <span className={cn('text-o-titulo2 font-bold tabular-nums', lista.length ? COLOR[g].texto : 'text-oficina-texto3')}>{lista.length}</span>
              </p>
              <p className="text-o-nota leading-snug text-oficina-texto3">{t(`salaControl.taller.gruposQue.${g}`)}</p>
              <Puntos trabajos={lista} grupo={g} />
            </div>
          );
        })}
      </div>
    </Caja>
  );
}

/** Lo que entró cada día (14 días), apilado por sala; cifra encima de hoy y del día más alto; raya de la media. */
function Ritmo({ d }: { d: TallerSala }) {
  const salas = d.salas.map((s) => s.nombre);
  const W = 640;
  const H = 200;
  const ABAJO = 22;
  const ARRIBA = 20;
  const max = Math.max(1, ...d.ritmo.map((x) => x.n));
  const ancho = W / Math.max(1, d.ritmo.length);
  const alto = (n: number) => ((H - ABAJO - ARRIBA) * n) / max;
  const suelo = H - ABAJO;
  const media = d.ritmo.reduce((a, x) => a + x.n, 0) / Math.max(1, d.ritmo.length);
  const masAlto = d.ritmo.reduce((m, x, i) => (x.n > (d.ritmo[m]?.n ?? 0) ? i : m), 0);
  return (
    <Caja titulo={t('salaControl.taller.ritmo')} sub={t('salaControl.taller.ritmoSub', { media: Math.round(media) })}>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label={t('salaControl.taller.ritmoAria', { media: Math.round(media) })} data-testid="taller-ritmo">
        <line x1="0" x2={W} y1={suelo} y2={suelo} className="stroke-oficina-linea" />
        {d.ritmo.map((x, i) => {
          const hoy = i === d.ritmo.length - 1;
          const nombreDia = hoy ? t('salaControl.obra.hoy') : diaCorto(x.dia);
          const etiqueta = `${nombreDia}: ${x.n} · ${salas.map((s) => `${s} ${x.porSala[s] ?? 0}`).join(' · ')}`;
          let tope = suelo;
          const trozos = salas.map((s, k) => {
            const h = alto(x.porSala[s] ?? 0);
            if (h <= 0) return null;
            tope -= h;
            return { s, k, y: tope, h };
          });
          const ultimo = [...trozos].reverse().find(Boolean);
          return (
            <g key={x.dia} className="group/barra">
              <title>{etiqueta}</title>
              <rect x={i * ancho} y={0} width={ancho} height={H} className="fill-transparent group-hover/barra:fill-oficina-hover/40" />
              {trozos.map((z) =>
                z ? (
                  <rect
                    key={z.s}
                    x={i * ancho + ancho * 0.16}
                    y={z.y + (z === ultimo ? 0 : 1)}
                    width={ancho * 0.68}
                    height={Math.max(1, z.h - (z === ultimo ? 0 : 2))}
                    rx={z === ultimo ? 3 : 0}
                    className={COLOR_SALA[z.k] ?? 'fill-oficina-texto3'}
                  />
                ) : null,
              )}
              {i === masAlto || hoy ? (
                <text x={i * ancho + ancho / 2} y={suelo - alto(x.n) - 5} textAnchor="middle" className="fill-oficina-texto text-o-texto font-semibold">
                  {x.n}
                </text>
              ) : null}
              {(d.ritmo.length - 1 - i) % 3 === 0 ? (
                <text x={i * ancho + ancho / 2} y={H - 4} textAnchor="middle" className="fill-oficina-texto3 text-o-sub">
                  {nombreDia}
                </text>
              ) : null}
            </g>
          );
        })}
        <line x1="0" x2={W} y1={suelo - alto(media)} y2={suelo - alto(media)} className="pointer-events-none stroke-oficina-texto2" strokeWidth="1.5" strokeDasharray="4 3" />
      </svg>
      <ul className="mt-1 flex flex-wrap gap-x-4 text-o-sub text-oficina-texto2">
        {salas.map((s, k) => (
          <li key={s} className="flex items-center gap-1.5">
            <span aria-hidden className={cn('h-3 w-3 rounded-sm', PUNTO_SALA[k] ?? 'bg-oficina-texto3')} />
            {s}
          </li>
        ))}
        <li className="flex items-center gap-1.5">
          <span aria-hidden className="w-4 border-t-2 border-dashed border-oficina-texto2" />
          {t('salaControl.taller.media')}
        </li>
      </ul>
    </Caja>
  );
}

const TRAMOS: TramoOlvidoTaller[] = ['menos3', 'de3a7', 'de7a14', 'mas14'];
const OPACIDAD_TRAMO = ['opacity-40', 'opacity-60', 'opacity-80', 'opacity-100'];
/** Cuánto llevan olvidados los trabajos sin nadie: una barra por tramo, cada vez más intensa (a los 14 días se recogen). */
function Olvido({ d }: { d: TallerSala }) {
  const max = Math.max(1, ...TRAMOS.map((k) => d.olvido[k]));
  const total = TRAMOS.reduce((a, k) => a + d.olvido[k], 0);
  return (
    <Caja titulo={t('salaControl.taller.olvido')} sub={t('salaControl.taller.olvidoSub', { n: total })}>
      <ul className="flex flex-col gap-2.5 pt-1" data-testid="taller-olvido">
        {TRAMOS.map((k, i) => (
          <li key={k} className="grid grid-cols-[8rem_1fr_2rem] items-center gap-2 text-o-sub">
            <span className="text-oficina-texto2">{t(`salaControl.taller.tramos.${k}`)}</span>
            <span className="h-5 overflow-hidden rounded bg-oficina-panel">
              <span className={cn('block h-full rounded bg-oficina-bloqueado', OPACIDAD_TRAMO[i])} style={{ width: `${(100 * d.olvido[k]) / max}%` }} />
            </span>
            <span className="text-right font-semibold tabular-nums">{d.olvido[k]}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-o-nota text-oficina-texto3">{t('salaControl.taller.olvidoNota')}</p>
    </Caja>
  );
}

/** Barra de las líneas de trabajo de una sala en GitHub, por quién las lleva; cada trozo con su nombre al pasar por encima. */
function BarraLineas({ s }: { s: SalaTallerSala }) {
  const presentes = LINEAS.filter((l) => s.lineas[l]);
  if (!presentes.length) return null;
  return (
    <div className="flex h-5 w-full gap-0.5" role="img" aria-label={presentes.map((l) => `${t(`salaControl.taller.lineas.${l}`)} ${s.lineas[l]}`).join(', ')}>
      {presentes.map((l) => (
        <Pista key={l} texto={`${t(`salaControl.taller.lineas.${l}`)}: ${s.lineas[l]}`} className="h-full min-w-2" style={{ flexGrow: s.lineas[l], flexBasis: 0 }}>
          <span className={cn('block h-full w-full rounded-sm', COLOR_LINEA[l].punto)} />
        </Pista>
      ))}
    </div>
  );
}

/** La versión oficial de la sala (master en Venuz): si sus pruebas están en verde y cuál fue su último cambio. */
function VersionOficial({ s, ahora }: { s: SalaTallerSala; ahora: number }) {
  const p = s.principal;
  if (!p) return <p className="text-o-sub text-oficina-texto3">{t('salaControl.taller.aviso.sin_github')}</p>;
  const Icono = { verde: CheckCircle2, rojo: XCircle, en_curso: CircleDashed, sin_datos: CircleDashed }[p.pruebas];
  const color = { verde: 'text-oficina-trabajando', rojo: 'text-oficina-error', en_curso: 'text-oficina-acento', sin_datos: 'text-oficina-texto3' }[p.pruebas];
  return (
    <div className="flex items-start gap-3 rounded-o-s bg-oficina-panel px-3 py-2.5" data-testid="taller-oficial">
      <Icono aria-hidden className={cn('mt-0.5 h-6 w-6 shrink-0', color)} />
      <div className="min-w-0 flex-1">
        <p className="text-o-texto font-semibold">
          {t('salaControl.taller.oficial', { nombre: p.nombre })} · <span className={color}>{t(`salaControl.taller.pruebas.${p.pruebas}`)}</span>
        </p>
        {p.ultimo ? (
          <p className="truncate text-o-sub text-oficina-texto2" title={p.ultimo.titulo}>
            {t('salaControl.taller.ultimoCambio', { edad: edad(segundosDesde(p.ultimo.en, ahora)), titulo: p.ultimo.titulo })}
          </p>
        ) : null}
      </div>
      {s.enlaces ? <EnlaceGitHub href={s.enlaces.principal} /> : null}
    </div>
  );
}

function EnlaceGitHub({ href, texto }: { href: string; texto?: string }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="inline-flex min-h-11 shrink-0 items-center gap-1 text-o-nota text-oficina-enlace hover:underline sm:min-h-0">
      {texto ?? t('salaControl.taller.verEnGitHub')}
      <ExternalLink aria-hidden className="h-3.5 w-3.5" />
    </a>
  );
}

function Subtitulo({ titulo, que, derecha }: { titulo: string; que: string; derecha?: ReactNode }) {
  return (
    <div className="mb-2 mt-4 flex flex-wrap items-end justify-between gap-x-3">
      <div>
        <p className="text-o-texto font-semibold">{titulo}</p>
        <p className="text-o-nota text-oficina-texto3">{que}</p>
      </div>
      {derecha}
    </div>
  );
}

function Ficha({ w, ahora, abrir }: { w: TrabajoTaller; ahora: number; abrir: (agentId: string) => void }) {
  const g = GRUPO_DE[w.paso];
  const agentId = w.agente?.vivo ? w.agente.agentId : null;
  const contenido = (
    <>
      <IconoEncargo icono={w.agente?.icono ?? null} semilla={w.agente?.semilla ?? w.id} tamano={40} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-o-texto font-medium" title={w.titulo}>
          {w.titulo}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-o-nota">
          <span className={cn('inline-flex items-center gap-1 font-semibold', COLOR[g].texto)}>
            <span aria-hidden className={cn('h-2 w-2 rounded-full', COLOR[g].punto)} />
            {t(`salaControl.taller.pasos.${w.paso}`)}
          </span>
          {w.motivo ? <span className="text-oficina-error-claro">{t(`salaControl.taller.motivos.${w.motivo}`)}</span> : null}
          <span className="text-oficina-texto3">{t('salaControl.taller.activo', { edad: edad(segundosDesde(w.movidoEn, ahora)) })}</span>
        </span>
        <span className="mt-0.5 block text-o-nota text-oficina-texto3">{w.urlRama ? t('salaControl.taller.yaEnGitHub') : t('salaControl.taller.soloEnSuCopia')}</span>
      </span>
    </>
  );
  const marco = cn('flex min-h-11 w-full items-center gap-3 rounded-o-s border-l-[3px] bg-oficina-panel px-3 py-2 text-left', COLOR[g].borde);
  return (
    <li>
      {agentId ? (
        <button
          type="button"
          onClick={() => abrir(agentId)}
          aria-label={t('salaControl.taller.abrirChat', { tema: w.agente!.tema })}
          className={cn(marco, 'outline-none hover:bg-oficina-hover focus-visible:ring-2 focus-visible:ring-oficina-acento/60')}
        >
          {contenido}
        </button>
      ) : (
        <div className={marco}>{contenido}</div>
      )}
    </li>
  );
}

function FilaOlvidada({ w, ahora }: { w: TrabajoTaller; ahora: number }) {
  const quien = w.agente ? t('salaControl.taller.dejo', { tema: w.agente.tema }) : t('salaControl.taller.deNadie');
  const detalle = [
    w.agente && w.agente.tema === w.titulo ? null : quien,
    t('salaControl.taller.movido', { edad: edad(segundosDesde(w.movidoEn, ahora)) }),
    w.seRecogeEn ? t('salaControl.taller.seRecoge', { fecha: diaPau(w.seRecogeEn) }) : null,
  ].filter(Boolean);
  return (
    <li className="flex items-start gap-2.5 border-b border-oficina-hover pb-1.5 last:border-0">
      <span aria-hidden className={cn('mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full', COLOR.sin_nadie.punto)} />
      <span className="min-w-0">
        <span className="block truncate text-o-sub" title={w.titulo}>
          {w.titulo}
        </span>
        <span className="block text-o-nota text-oficina-texto3">
          {detalle.join(' · ')}
          {w.propuesta ? (
            <>
              {' · '}
              <a href={w.propuesta.url} target="_blank" rel="noreferrer" className="text-oficina-enlace hover:underline">
                {t('salaControl.taller.verPropuesta')}
              </a>
            </>
          ) : null}
        </span>
      </span>
    </li>
  );
}

function SalaBloque({ s, ahora, abrir }: { s: SalaTallerSala; ahora: number; abrir: (agentId: string) => void }) {
  const agentes = s.trabajos.filter((w) => w.agente?.vivo);
  const abandonadas = s.trabajos.filter((w) => w.paso === 'sin_nadie');
  return (
    <section className="rounded-o-m bg-oficina-elevada px-4 py-3" data-testid={`taller-sala-${s.projectId}`}>
      <h4 className="text-o-titulo3 font-semibold">{s.nombre}</h4>
      <div className="mt-2">
        <VersionOficial s={s} ahora={ahora} />
      </div>

      <Subtitulo titulo={t('salaControl.taller.agentesTitulo', { n: agentes.length })} que={t('salaControl.taller.agentesQue')} />
      {agentes.length ? (
        <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {agentes.map((w) => (
            <Ficha key={w.id} w={w} ahora={ahora} abrir={abrir} />
          ))}
        </ul>
      ) : (
        <p className="text-o-sub text-oficina-texto3">{t('salaControl.taller.sinAgentes')}</p>
      )}

      <Subtitulo
        titulo={t('salaControl.taller.lineasTitulo', { n: s.lineas.total })}
        que={t('salaControl.taller.lineasQue')}
        derecha={s.enlaces ? <EnlaceGitHub href={s.enlaces.ramas} texto={t('salaControl.taller.verLineas')} /> : null}
      />
      <BarraLineas s={s} />
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-o-sub">
        {LINEAS.map((l) => (
          <li key={l} className="flex items-center gap-1.5">
            <span aria-hidden className={cn('h-2.5 w-2.5 rounded-full', COLOR_LINEA[l].punto)} />
            <span className="text-oficina-texto2">{t(`salaControl.taller.lineas.${l}`)}</span>
            <span className="font-semibold tabular-nums">{s.lineas[l]}</span>
          </li>
        ))}
      </ul>
      {abandonadas.length ? (
        <div className="mt-3">
          <p className="mb-1.5 text-o-nota font-semibold text-oficina-texto3">{t('salaControl.taller.olvidados', { n: abandonadas.length })}</p>
          <ListaPlegable
            items={abandonadas}
            primeras={3}
            clave={(w) => w.id}
            pinta={(w) => <FilaOlvidada w={w} ahora={ahora} />}
            yMas={(n) => t('salaControl.taller.yMas', { n })}
            verMenos={t('salaControl.taller.verMenos')}
          />
        </div>
      ) : null}
    </section>
  );
}

function Entraron({ d }: { d: TallerSala }) {
  const todas = d.salas.flatMap((s) => s.entraron.map((e) => ({ ...e, sala: s.nombre }))).sort((a, b) => (a.en < b.en ? 1 : -1));
  return (
    <Caja titulo={t('salaControl.taller.entraron')}>
      {todas.length ? (
        <ListaPlegable
          items={todas}
          primeras={6}
          clave={(e) => e.url}
          yMas={(n) => t('salaControl.taller.yMas', { n })}
          verMenos={t('salaControl.taller.verMenos')}
          pinta={(e) => (
            <li className="grid grid-cols-[auto_1fr] gap-x-3 border-b border-oficina-hover pb-1 text-o-sub last:border-0">
              <span className="whitespace-nowrap text-o-nota text-oficina-texto3">{cuandoPau(e.en)}</span>
              <span>
                <span className="text-oficina-texto2">{e.sala} · </span>
                {e.titulo}
              </span>
            </li>
          )}
        />
      ) : (
        <p className="text-o-sub text-oficina-texto3">{t('salaControl.taller.entraronNada')}</p>
      )}
    </Caja>
  );
}

export function TallerVista({ d, ahora = Date.now(), abrir }: { d: TallerSala; ahora?: number; abrir: (agentId: string) => void }) {
  return (
    <div className="flex flex-col gap-3">
      <Rayas />
      <Portada d={d} />
      <QueMirar d={d} />
      {d.salas.map((s) => (
        <SalaBloque key={s.projectId} s={s} ahora={ahora} abrir={abrir} />
      ))}
      <Camino d={d} />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.4fr_1fr]">
        <Ritmo d={d} />
        <Olvido d={d} />
      </div>
      <Entraron d={d} />
    </div>
  );
}

export function Taller() {
  const { foto, fallo, actualizarYa, actualizando } = useSalaRincon<TallerSala>('taller');
  const navigate = useNavigate();
  const location = useLocation();
  if (fallo && !foto) return <Aviso tono="error">{t('salaControl.errorCarga')}</Aviso>;
  if (calculandoPrimeraVez(foto)) return <Calculando />;
  if (!foto) return null;
  const motivo = foto.error ? t(`salaControl.taller.errores.${foto.error.mensaje}` as TKey) : null;
  if (!foto.datos) {
    return motivo ? <Aviso tono="error">{t('salaControl.rincones.sinDatosPorError', { motivo })}</Aviso> : <SinDatos texto={t('salaControl.taller.sinDatos')} />;
  }
  const desfasada = foto.vieja || !!foto.error;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h3 className="text-o-titulo3 font-semibold">{t('salaControl.taller.titulo')}</h3>
        <div className="flex items-center gap-3">
          {foto.edad_s !== null ? <p className="text-o-nota text-oficina-texto3">{t('salaControl.taller.sub', { edad: edad(foto.edad_s) })}</p> : null}
          <button
            type="button"
            onClick={actualizarYa}
            disabled={actualizando}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-o-s bg-oficina-panel px-3 text-o-sub text-oficina-texto outline-none hover:bg-oficina-hover focus-visible:ring-2 focus-visible:ring-oficina-acento/60 disabled:text-oficina-texto3 sm:min-h-9"
          >
            <RefreshCw aria-hidden className={cn('h-4 w-4', actualizando && 'animate-spin')} />
            {actualizando ? t('salaControl.taller.actualizando') : t('salaControl.taller.actualizarYa')}
          </button>
        </div>
      </div>
      {desfasada ? (
        <Aviso tono="aviso">
          {t('salaControl.rincones.desfasado', { edad: edad(foto.edad_s ?? 0), motivo: motivo ?? t('salaControl.taller.errores.error') })}
        </Aviso>
      ) : null}
      <EnMarcha taller={foto.datos} />
      <div className={cn(desfasada && 'opacity-60')}>
        <TallerVista d={foto.datos} abrir={(id) => navigate(rutaDelAgente(id, location))} />
      </div>
    </div>
  );
}
